import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Bot, Check, Cpu, FileText, Info, Shield } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import { useProjectSettings } from '@/app/project-settings/layout/ProjectSettingsLayout';
import { useProjectName } from '@/app/layout/ProjectNameContext';
import { isValidAppId as isValidLeanIXId, normalizeAppId } from '@/app/onboarding-secops/validation';

interface ProjectMetadata {
  appName: string;
  leanIXId: string;
  projectPhase: string;
  criticality: string;
  dataClassification: string;
  complianceTags: string[];
  languages: string[];
  frameworks: string[];
  buildTools: string[];
}

const PROJECT_PHASES = ['PLANNING', 'ACTIVE_DEVELOPMENT', 'KTLO', 'MAINTENANCE', 'SUNSET'];
const CRITICALITY_OPTIONS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const DATA_CLASSIFICATION_OPTIONS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'STRICTLY_CONFIDENTIAL'];
const COMPLIANCE_OPTIONS = ['ISO-27001', 'SOC-2', 'GDPR', 'PCI-DSS', 'HIPAA', 'TISAX'];
const LANGUAGE_OPTIONS = ['Java', 'TypeScript', 'JavaScript', 'Python', 'Go', 'Kotlin', 'C#', 'C++', 'Rust'];
const FRAMEWORK_OPTIONS = ['Spring Boot', 'React', 'Angular', 'Vue', 'Next.js', 'Express', 'Django', 'Flask', '.NET'];
const BUILD_TOOL_OPTIONS = ['Gradle', 'Maven', 'Vite', 'Webpack', 'npm', 'Yarn', 'pnpm', 'Bazel'];

type AiEngine = 'claude' | 'devin' | 'deepagent';

const AI_ENGINE_OPTIONS: { value: AiEngine; label: string; description: string }[] = [
  { value: 'claude', label: 'Claude', description: 'Default — uses Anthropic Claude via AWS Bedrock' },
  // { value: 'devin', label: 'Devin', description: 'Uses Devin CLI for AI-powered code analysis' },
  // { value: 'deepagent', label: 'DeepAgent', description: 'Uses DeepAgent (Azure OpenAI) for analysis' },
];

export default function ProjectMetadataPage() {
  const { appId } = useParams<{ appId: string }>();
  const theme = useTheme();
  const { projectName } = useProjectSettings();

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const [metadata, setMetadata] = useState<ProjectMetadata>({
    appName: '',
    leanIXId: 'APP-',
    projectPhase: 'PLANNING',
    criticality: 'MEDIUM',
    dataClassification: 'INTERNAL',
    complianceTags: [],
    languages: [],
    frameworks: [],
    buildTools: [],
  });

  // AI Engine state
  const [aiEngine, setAiEngine] = useState<AiEngine>('claude');
  const [savingEngine, setSavingEngine] = useState(false);


  // Pre-populate from shared context (already loaded at project page level)
  const { projectConfig } = useProjectName();
  useEffect(() => {
    if (projectConfig) {
      setMetadata({
        appName: projectConfig.appName || '',
        leanIXId: projectConfig.leanIXId ? normalizeAppId(projectConfig.leanIXId) : 'APP-',
        projectPhase: projectConfig.metadata?.projectPhase || 'PLANNING',
        criticality: projectConfig.metadata?.criticality || 'MEDIUM',
        dataClassification: projectConfig.metadata?.dataClassification || 'INTERNAL',
        complianceTags: projectConfig.metadata?.complianceTags || [],
        languages: projectConfig.metadata?.languages || [],
        frameworks: projectConfig.metadata?.frameworks || [],
        buildTools: projectConfig.metadata?.buildTools || [],
      });
      const configuredEngine = projectConfig.ai_engine_config?.sentinel?.engine as AiEngine | undefined;
      if (configuredEngine && AI_ENGINE_OPTIONS.some(o => o.value === configuredEngine)) {
        setAiEngine(configuredEngine);
      }
    }
  }, [projectConfig]);


  const handleSave = async () => {
    if (!metadata.appName.trim()) {
      setSnackbar({
        open: true,
        message: 'Project name is required',
        severity: 'error',
      });
      return;
    }

    if (!isValidLeanIXId(metadata.leanIXId)) {
      setSnackbar({
        open: true,
        message: 'LeanIX ID must follow the format APP-<number> (e.g. APP-12345)',
        severity: 'error',
      });
      return;
    }

    setSaving(true);
    try {
      await sentinelNetwork.patch(`/config/project/${appId}`, {
        appName: metadata.appName,
        leanIXId: metadata.leanIXId,
        metadata: {
          projectPhase: metadata.projectPhase,
          criticality: metadata.criticality,
          dataClassification: metadata.dataClassification,
          complianceTags: metadata.complianceTags,
          languages: metadata.languages,
          frameworks: metadata.frameworks,
          buildTools: metadata.buildTools,
        },
      });

      setSnackbar({
        open: true,
        message: 'Project metadata saved successfully',
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Failed to save metadata:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to save metadata',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 1 }}
          >
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
              }}
            >
              <FileText size={24} color={theme.palette.primary.main} />
            </Box>
            <Typography variant="h5" fontWeight={700}>
              Project Metadata
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Manage project name, identifiers, and external links
          </Typography>
        </Box>

        {/* Basic Info */}
        <Card
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider' }}
        >
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Basic Information
            </Typography>
            <Stack spacing={2.5}>
              <TextField
                label="Project Name"
                value={metadata.appName}
                onChange={(e) =>
                  setMetadata({ ...metadata, appName: e.target.value })
                }
                fullWidth
                required
                size="small"
                helperText="Display name for this project"
              />
              <TextField
                label="LeanIX ID (APP ID)"
                value={metadata.leanIXId}
                onChange={(e) =>
                  setMetadata({ ...metadata, leanIXId: normalizeAppId(e.target.value) })
                }
                placeholder="APP-12345"
                fullWidth
                size="small"
                required
                error={metadata.leanIXId.trim() !== 'APP-' && !isValidLeanIXId(metadata.leanIXId)}
                helperText={
                  metadata.leanIXId.trim() !== 'APP-' && !isValidLeanIXId(metadata.leanIXId)
                    ? 'Must be APP- followed by exactly 5 digits (e.g. APP-12345)'
                    : 'LeanIX application identifier (required)'
                }
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Project Phase */}
        <Card
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider' }}
        >
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Project Phase
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {PROJECT_PHASES.map((p) => {
                const selected = metadata.projectPhase === p;
                return (
                  <Chip
                    key={p}
                    label={p.replace(/_/g, ' ')}
                    onClick={() => setMetadata({ ...metadata, projectPhase: p })}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: selected ? 600 : 400,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  />
                );
              })}
            </Stack>
          </CardContent>
        </Card>

        {/* Classification */}
        <Card
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider' }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Box
                sx={{
                  p: 0.75,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  display: 'flex',
                }}
              >
                <Shield size={20} color={theme.palette.warning.main} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Classification
              </Typography>
            </Stack>
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                  <InputLabel>Criticality</InputLabel>
                  <Select
                    value={metadata.criticality}
                    label="Criticality"
                    onChange={(e) => setMetadata({ ...metadata, criticality: e.target.value })}
                  >
                    {CRITICALITY_OPTIONS.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                  <InputLabel>Data Classification</InputLabel>
                  <Select
                    value={metadata.dataClassification}
                    label="Data Classification"
                    onChange={(e) => setMetadata({ ...metadata, dataClassification: e.target.value })}
                  >
                    {DATA_CLASSIFICATION_OPTIONS.map((d) => (
                      <MenuItem key={d} value={d}>{d.replace(/_/g, ' ')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={COMPLIANCE_OPTIONS}
                value={metadata.complianceTags}
                onChange={(_, v) => setMetadata({ ...metadata, complianceTags: v as string[] })}
                renderInput={(params) => <TextField {...params} label="Compliance Tags" />}
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Tech Stack */}
        <Card
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider' }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Box
                sx={{
                  p: 0.75,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  display: 'flex',
                }}
              >
                <Cpu size={20} color={theme.palette.info.main} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Tech Stack
              </Typography>
            </Stack>
            <Stack spacing={2}>
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={LANGUAGE_OPTIONS}
                value={metadata.languages}
                onChange={(_, v) => setMetadata({ ...metadata, languages: v as string[] })}
                renderInput={(params) => <TextField {...params} label="Languages" />}
              />
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={FRAMEWORK_OPTIONS}
                value={metadata.frameworks}
                onChange={(_, v) => setMetadata({ ...metadata, frameworks: v as string[] })}
                renderInput={(params) => <TextField {...params} label="Frameworks" />}
              />
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={BUILD_TOOL_OPTIONS}
                value={metadata.buildTools}
                onChange={(_, v) => setMetadata({ ...metadata, buildTools: v as string[] })}
                renderInput={(params) => <TextField {...params} label="Build Tools" />}
              />
            </Stack>
          </CardContent>
        </Card>


        {/* AI Engine */}
        <Card
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider' }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Box
                sx={{
                  p: 0.75,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  display: 'flex',
                }}
              >
                <Bot size={20} color={theme.palette.secondary.main} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                AI Engine
              </Typography>
              <Tooltip title="Select which AI engine Sentinel uses when extracting security reports for this project. Can be overridden per scan at trigger time." arrow>
                <Info size={14} style={{ color: theme.palette.text.secondary, cursor: 'default' }} />
              </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Default engine for security report extraction. Can be overridden per scan.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>Default Engine</InputLabel>
                <Select
                  value={aiEngine}
                  label="Default Engine"
                  onChange={(e) => setAiEngine(e.target.value as AiEngine)}
                >
                  {AI_ENGINE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Stack>
                        <Typography variant="body2" fontWeight={500}>{opt.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                disabled={savingEngine}
                startIcon={savingEngine ? <CircularProgress size={14} color="inherit" /> : <Check size={14} />}
                onClick={async () => {
                  setSavingEngine(true);
                  try {
                    await sentinelNetwork.patch(`/config/project/${appId}`, {
                      ai_engine_config: { sentinel: { engine: aiEngine } },
                    });
                    setSnackbar({ open: true, message: 'AI engine setting saved', severity: 'success' });
                  } catch (err: any) {
                    setSnackbar({ open: true, message: err?.response?.data?.message || 'Failed to save AI engine', severity: 'error' });
                  } finally {
                    setSavingEngine(false);
                  }
                }}
                sx={{ height: 40, mt: 0.5 }}
              >
                {savingEngine ? 'Saving...' : 'Save Engine'}
              </Button>
            </Stack>
            {aiEngine !== 'claude' && (
              <Alert severity="info" sx={{ mt: 2, py: 0.5 }} icon={<Bot size={14} />}>
                <strong>{AI_ENGINE_OPTIONS.find(o => o.value === aiEngine)?.label}</strong> requires its credentials to be present in the Sentinel secrets. If not configured, it will fall back to Claude automatically.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Check size={20} />
              )
            }
            onClick={handleSave}
            disabled={
              saving ||
              !metadata.appName.trim() ||
              !isValidLeanIXId(metadata.leanIXId)
            }
            sx={{ fontWeight: 600 }}
          >
            {saving ? 'Saving...' : 'Save Metadata'}
          </Button>
        </Box>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
